// src/components/Actions/ReviewActionForm.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
} from '@mui/material';
import { CheckCircle, Description, Visibility } from '@mui/icons-material';
import { acknowledgeReview, getReviewActionDetails, AcknowledgeReviewResponse, ReviewActionDetails, ReviewApiError } from '../../services/reviewApi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  dismissAfterActionForm,
  EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE,
  shouldNavigateIntoPortfolioAfterAction,
} from '../../utils/actionFormNavigation';
import { apiService } from '../../services/api';
import { shouldOpenSharePointDirectly } from '../../utils/sharePointLinkUtils';
import { resolveDownloadFileName } from '../../utils/downloadFileName';
import { DocumentComments } from '../ProjectExecution/DocumentComments';
import { ActionComments } from '../ProjectExecution/ActionComments';
import { DocumentAssociation } from '../../types';

interface ReviewActionFormProps {
  token: string | null; // JWT token from URL query parameter (optional for execution view access)
  actionId?: number | null; // Action ID for execution view access (optional)
  projectId?: number | null; // Project ID for execution view access (optional)
  onSuccess?: () => void; // Callback after successful acknowledgment
  onError?: (error: string) => void; // Callback for errors
}

export default function ReviewActionForm({ token, actionId, projectId, onSuccess, onError }: ReviewActionFormProps) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [response, setResponse] = useState<AcknowledgeReviewResponse | null>(null);
  const [actionDetails, setActionDetails] = useState<ReviewActionDetails | null>(null);
  const [documentAssociations, setDocumentAssociations] = useState<DocumentAssociation[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  
  // Verify authentication when using actionId (execution view access)
  useEffect(() => {
    if (!token && actionId && !isAuthenticated) {
      setError('You must be logged in to acknowledge review actions from the execution view. Please log in and try again.');
    }
  }, [token, actionId, isAuthenticated]);

  const handleSubmit = async () => {
    // Validate comment length
    if (comment.length > 2000) {
      setError('Comment cannot exceed 2000 characters');
      return;
    }

    // Token or actionId is required for acknowledgment
    if (!token && !actionId) {
      setError('Invalid request. Token or actionId is required.');
      return;
    }
    
    // For execution view access, verify user is authenticated
    if (!token && actionId && !isAuthenticated) {
      setError('You must be logged in to acknowledge review actions. Please refresh the page and log in.');
      return;
    }
    

    setError(null);
    setLoading(true);

    try {
      const data = {
        comment: comment.trim() || undefined,
      };

      // Backend now supports both token-based and actionId-based acknowledgment
      const result = await acknowledgeReview(token, data, actionId || undefined);
      setResponse(result);
      setSuccess(true);

      // Call success callback
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      const apiError = err as ReviewApiError;
      const errorMessage = apiError.message || 'Failed to acknowledge review. Please try again.';
      setError(errorMessage);
      
      // Call error callback
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 2000) {
      setComment(value);
      setError(null);
    }
  };

  // Fetch action details on component mount
  useEffect(() => {
    const fetchDetails = async () => {
      setLoadingDetails(true);
      
      try {
        // getReviewActionDetails now supports both token and actionId
        // It handles errors gracefully and returns empty details if fetch fails
        const details = await getReviewActionDetails(token, actionId || undefined);
        setActionDetails(details);
      } catch (err) {
        console.warn('Could not fetch review action details:', err);
        // Set minimal details if we have actionId
        if (actionId) {
          setActionDetails({
            actionId: actionId,
            actionType: 'REVIEW',
          });
        } else if (token) {
          // Fallback for token-based access
          setActionDetails({
            actionId: 0,
            actionType: 'REVIEW',
          });
        }
      }
      
      setLoadingDetails(false);
    };

    if (token || actionId) {
      fetchDetails();
    } else {
      setLoadingDetails(false);
    }
  }, [token, actionId]);

  // Fetch document associations when actionId is available
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!actionId) {
        return; // Can't fetch documents without actionId
      }

      setLoadingDocuments(true);
      try {
        const docs = await apiService.getActionDocuments(actionId);
        // Filter to only non-placeholder documents with spItemId
        const validDocs = docs.filter(doc => !doc.isPlaceholder && doc.spItemId);
        setDocumentAssociations(validDocs);
      } catch (err) {
        console.warn('Could not fetch document associations:', err);
        setDocumentAssociations([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    if (actionId) {
      fetchDocuments();
    }
  }, [actionId]);

  const reviewDocuments = documentAssociations.filter((doc) => doc.spItemId);
  const reviewDocument = reviewDocuments.length > 0 ? reviewDocuments[0] : null;

  // Success state
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Review Acknowledged Successfully
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for reviewing the document. Your acknowledgment has been recorded.
          </Typography>
          {response?.comment && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>
                Your Comment:
              </Typography>
              <Typography variant="body2">{response.comment}</Typography>
            </Box>
          )}
          {!shouldNavigateIntoPortfolioAfterAction(user) ? (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE}
            </Typography>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                dismissAfterActionForm(navigate, user, {
                  internalProjectId: projectId ?? undefined,
                });
              }}
              sx={{ mt: 2 }}
            >
              {projectId ? 'Return to Execution View' : 'Return to Dashboard'}
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  // Form state
  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Review Action - Acknowledgment
        </Typography>
        
        <Divider sx={{ my: 3 }} />

        {/* Document/Project/Phase/Task Information (Sprint H6: Phase and Task context, note removed) */}
        {loadingDetails ? (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading document information...
            </Typography>
          </Box>
        ) : actionDetails && (actionDetails.documentName || actionDetails.projectName || actionDetails.phaseName || actionDetails.taskName) ? (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            {actionDetails.documentName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Description sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Document:</strong> {actionDetails.documentName}
                </Typography>
              </Box>
            )}
            {actionDetails.projectName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Project:</strong> {actionDetails.projectName}
              </Typography>
            )}
            {(actionDetails.phaseName || actionDetails.phaseDescription) && (
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Phase:</strong> {actionDetails.phaseName || ''}
                </Typography>
                {actionDetails.phaseDescription && (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1, fontStyle: 'italic' }}>
                    {actionDetails.phaseDescription}
                  </Typography>
                )}
              </Box>
            )}
            {(actionDetails.taskName || actionDetails.taskDescription) && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Task:</strong> {actionDetails.taskName || ''}
                </Typography>
                {actionDetails.taskDescription && (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1, fontStyle: 'italic' }}>
                    {actionDetails.taskDescription}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : null}

        {/* Document Viewer Section */}
        {loadingDocuments ? (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading document...
            </Typography>
          </Box>
        ) : reviewDocuments.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Document{reviewDocuments.length > 1 ? 's' : ''} to Review:
            </Typography>
            {reviewDocuments.map((doc) => (
              <Box key={doc.actionDocumentId || doc.spItemId} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {`${doc.fileName || doc.documentTitle || 'Document'} – REVIEW`}
                  </Typography>
                  {doc.documentDescription && (
                    <Typography variant="caption" color="text.secondary">
                      {doc.documentDescription}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={async () => {
                    if (shouldOpenSharePointDirectly(user) && doc.webUrl) {
                      window.open(doc.webUrl!, '_blank');
                      return;
                    }
                    if (doc.spItemId) {
                      const nameOpts = {
                        fileName: doc.fileName,
                        documentTitle: doc.documentTitle,
                      };
                      try {
                        const { blob, fileName } = await apiService.viewFileWithInfo(
                          doc.spItemId,
                          'SHAREPOINT'
                        );
                        apiService.triggerBlobDownload(
                          blob,
                          resolveDownloadFileName({
                            headerFileName: fileName,
                            blob,
                            ...nameOpts,
                          })
                        );
                      } catch (err) {
                        console.error('Failed to view document:', err);
                        try {
                          const { blob, fileName } = await apiService.downloadFileWithInfo(
                            doc.spItemId,
                            'SHAREPOINT'
                          );
                          apiService.triggerBlobDownload(
                            blob,
                            resolveDownloadFileName({
                              headerFileName: fileName,
                              blob,
                              ...nameOpts,
                            })
                          );
                        } catch (downloadErr) {
                          console.error('Failed to download document:', downloadErr);
                          setError('Failed to open document. Please try again.');
                        }
                      }
                    }
                  }}
                >
                  View
                </Button>
              </Box>
            ))}
          </Box>
        ) : null}

        {actionId ? <ActionComments actionId={actionId} title="Action comments" /> : null}

        {/* Previous Comments Section */}
        {reviewDocument && reviewDocument.spItemId && (
          <Box sx={{ mb: 3 }}>
            <DocumentComments
              file={{
                spItemId: reviewDocument.spItemId,
                siteId: '', // Will use spItemId which is preferred
                driveId: '',
                itemId: '',
              }}
            />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Instructions:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please review the document and acknowledge that you have reviewed it. You may optionally add a comment.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Comment (Optional)"
            multiline
            rows={6}
            fullWidth
            value={comment}
            onChange={handleCommentChange}
            disabled={loading}
            placeholder="Add an optional comment..."
            helperText={`${comment.length}/2000 characters`}
            inputProps={{
              maxLength: 2000,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (onError) {
                onError('Review acknowledgment cancelled');
              }
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Mark as Reviewed'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

